import React from 'react';
import { Card, OverlayTrigger, Popover } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import './bloodwork-anemia-status.css';
import { BloodworkAnalysis } from '../../models/BloodworkAnalysis';
import {
  LipidParticleMap,
  lipidParticleMap,
} from '../../models/LipidParticleMap';
import { LipidParticleHero } from './lipid-particle-hero';
import {
  LIPID_AREA_SHORT,
  LIPID_BAND_LABEL,
  LIPID_BAND_SCORE,
  LIPID_RISK_AXIS_HIGH,
  LIPID_RISK_AXIS_LOW,
  LipidAreaAnalysis,
  LipidAreaResult,
  LipidBand,
  ResolvedPattern,
  rankedLipidAreas,
  resolveLipidAreas,
} from '../../models/LipidParticleAreas';
import { MARKER_ROW_STYLE } from './pattern-box-styles';
import { MUTED_PALETTE } from '../charts/chartPalette';
import { nmrLipoProfileVisible } from '../../utils/nmrLipoProfileVisibility';
import {
  BAND_REFERENCE_NOTE,
  LDL_P_BAND_NOTES,
  SMALL_LDL_P_BAND_NOTES,
  lpIrBandLabel,
} from '../../models/LipidParticleMap';
import { lpIrPercentileWording } from './lipid-particle-hero';

/**
 * Lipid Insights — the interpretation layer for the NMR LipoProfile
 * markers in the Lipid Balance section.
 *
 * Three blocks, the same shape the Thyroid panel uses: a hero, five ranked area
 * bars, then the pattern boxes.
 *
 *   HERO          the Particle Map (count against size) and the LP-IR strip
 *   AREA BARS     five areas, worst first, on the Thyroid likelihood ladder
 *   PATTERN BOXES at most one per area, plus the additive non-fasting note
 *
 * A pattern box can be a HOLD, which no other section has: the bar stays green
 * and the box says why a red standard-lipid marker is not carried by the
 * particle data. Three of the four holds exist for that, and they are the
 * reason this panel can talk a marker down as well as up.
 *
 * Same contract as every other Insights panel, so the parent column, the PDF
 * packer and the AI export can treat it like the rest:
 *
 *   shouldRender(ba)       whether it draws anything at all, so the parent can
 *                          drop the 300px column and let Results left-align
 *   exportInsights(ba)     serializable view for the raw-data export
 *   estimatedPdfHeight(ba) height before render, for the page packer
 *
 * ── Why the gauge vocabulary is absent ─────────────────────────────────────
 * Glucose, Thyroid, Adrenal and Hydration lead with "<condition> is <likely>
 * (n%)". That is right when inferring something unmeasured and wrong here:
 * LDL-P IS the measurement. So the hero states where the client falls, and the
 * one number on a 0-100 scale (LP-IR) is drawn as the lab's own percentile
 * strip rather than as a dial. See lipid-particle-hero.tsx.
 */

const arrow_up = require('../../assets/imgs/long-arrow-up-8x13.png');
const arrow_down = require('../../assets/imgs/long-arrow-down-8x12.png');

/*
 * The ▲/▼/= row under each box. Same arrow assets and same inline style as
 * every other panel's marker row, so a reader moving between sections sees one
 * idiom. `level` has no arrow asset and no glyph is used for it either: an "="
 * image would be a fourth thing to keep in sync for no gain, so a marker the
 * Logic column calls `=` prints its name alone.
 */
const ARROW: Record<'up' | 'down', any> = { up: arrow_up, down: arrow_down };
const ARROW_ALT: Record<'up' | 'down', string> = {
  up: '\u25b2',
  down: '\u25bc',
};

/*
 * The neutral mark for a marker sitting INSIDE optimal.
 *
 * Not a blank, which read as an omission, and not a made-up arrow, which
 * contradicted the bar beside it. A visible "=" says the same thing the bar
 * says: this one is in the optimal band.
 */
const LEVEL_STYLE: React.CSSProperties = {
  display: 'inline-block',
  width: '11px',
  margin: '0 3px',
  textAlign: 'center',
  color: '#6b645e',
  fontWeight: 400,
};

const arrowStyle: React.CSSProperties = {
  height: '11px',
  margin: '0 3px',
  verticalAlign: 'middle',
  position: 'relative',
  top: '-0.5px',
};

const BAR_HEIGHT = 12;
const TRACK_BG = '#e9ecf1';
const TRACK_INSET = 'inset 0 0 0 1px rgba(0,0,0,0.06)';

/*
 * Where one band ends and the next begins, as a percentage of the track.
 *
 * A LADDER bar can only ever be one of four lengths (the band scores 25 / 60 /
 * 80 / 95), so without these the fill is a colored length with no scale behind it
 * and "moderate" against "low" is a judgement the reader has to take on trust
 * (Maryann, 2026-10-23). Drawn as hairlines ON the track, so the bar still
 * reads as one bar rather than as five segments.
 *
 * Taken from LIPID_BAND_SCORE rather than written out, so a change to the
 * ladder moves the lines with it. An area carrying its own published scale
 * (`area.scale`, Particle Burden) brings its own edges instead and ignores
 * these.
 */
const BAND_EDGES: number[] = [
  LIPID_BAND_SCORE.lower!,
  LIPID_BAND_SCORE.moderate!,
  LIPID_BAND_SCORE.higher!,
];

/** Bar fills: the bright zone colors, matching the hero's cells. */
const BAND_FILL: Record<LipidBand, string> = {
  notMeasured: '#e3e3e3',
  lower: MUTED_PALETTE.green,
  moderate: MUTED_PALETTE.yellow,
  higher: MUTED_PALETTE.orange,
  highest: MUTED_PALETTE.red,
};

/** The risk word, in a darker shade of the same hue so it reads as text. */
const BAND_TEXT: Record<LipidBand, string> = {
  notMeasured: '#b7b7b7',
  lower: '#3fa34d',
  moderate: '#c79a00',
  higher: '#e3741a',
  highest: '#d8472f',
};

/*
 * Labcorp's own axis, printed over every interpretive table on the report:
 * `<--Lower CVD Risk   Higher CVD Risk-->`. Without it the bars are five
 * colored lengths with no stated direction, and the reader has to infer that
 * longer is worse.
 */
/*
 * The two ends of the ranked-bar axis. 11px BOLD since 2026-10-26, up from
 * 9px regular: "let's make 'lower CVD risk' and 'higher CVD risk' in a bigger
 * font and bold" (Maryann). It is the only thing on the card that says what
 * the bars are ranked BY, and at 9px grey it read as a footnote.
 *
 * Measured: the pair comes to 173.1px at 11px bold, well inside 262px.
 */
const AXIS_CAPTION_STYLE: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  fontSize: '11px',
  fontWeight: 700,
  color: '#6b645e',
  marginBottom: '3px',
};

/*
 * The "explains" tag on a hold box. A hold is a finding whose effect is to keep
 * an area GREEN, so without a word for it the box reads as a contradiction: a
 * title about a marker being above range, sitting under a green bar.
 */
const HOLD_TAG_STYLE: React.CSSProperties = {
  fontSize: '9px',
  fontWeight: 400,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: '#6b645e',
  border: '1px solid #cfc9c3',
  borderRadius: '3px',
  padding: '0 3px',
  marginLeft: '5px',
  verticalAlign: 'middle',
};

const CONSISTENT_WITH_STYLE: React.CSSProperties = {
  fontWeight: 400,
  textAlign: 'left',
  fontSize: '11px',
};

type Props = {
  bloodworkAnalysis: BloodworkAnalysis;
};

/*
 * The lab's own condition on the LP-IR Score, stated ONCE.
 *
 * Labcorp prints "LP-IR Score is inaccurate if patient is non-fasting" on every
 * report. The first draft put that under every score, because `Bloodwork` has
 * no fasting field to check against — which made it fire on every test.
 * Maryann, 2026-10-23: a client having labs drawn is assumed to be fasting, so
 * a per-test caveat is noise. It stays in the panel as a footnote, once, below
 * everything, because it is the lab's stated condition on the number and the
 * practitioner should be able to find it.
 */
const LP_IR_FOOTNOTE =
  'Labcorp reports the LP-IR Score as accurate only on a fasting sample.';

const FOOTNOTE_STYLE: React.CSSProperties = {
  fontSize: '10px',
  color: '#6b645e',
  lineHeight: 1.4,
  marginTop: '8px',
  textAlign: 'left',
};

/*
 * The LP-IR reading, under the pattern boxes.
 *
 * It was a GAUGE above the map from 2026-10-26 until later the same day. See
 * the render method for why it is a line again.
 */
const LP_IR_LINE_STYLE: React.CSSProperties = {
  fontSize: '11px',
  color: '#3a3a3a',
  marginTop: '10px',
  textAlign: 'left',
};

export class LipidParticleInsights extends React.Component<Props, {}> {
  /**
   * Nothing to draw unless the Particle Map resolves at least one axis.
   *
   * The gate is deliberately NOT checked here as well. `nmrLipoProfileVisible()`
   * blanks the nine PanelMarkers upstream (populateLipidPanelMarkers), so in
   * prod `lipidParticleMap` already returns null and this answers false — one
   * switch, not two that can disagree. The explicit call in render() is for the
   * panel's own registration, which the parent reads before it has a map.
   */
  static shouldRender(ba: BloodworkAnalysis): boolean {
    if (!ba?.bloodwork) return false;
    return lipidParticleMap(ba) !== null;
  }

  /**
   * Serializable view of this panel for one test, mirroring render().
   *
   * NOT CALLED by the raw-data export yet: this panel is phase 1 and held off
   * prod, and held work stays out of the export in every build (see
   * AIExportBuilder.ts, which does the same for the Hydration panel). Kept
   * ready — the export needs one call and a schema entry in both prompt files.
   */
  static exportInsights(ba: BloodworkAnalysis): {
    map: LipidParticleMap;
    lpIrScore?: number;
  } | null {
    if (!ba?.bloodwork) return null;
    const map = lipidParticleMap(ba);
    if (!map) return null;
    const lpIr = ba.lpIrScore;
    const score =
      lpIr && typeof lpIr.hasValue === 'function' && lpIr.hasValue()
        ? lpIr.value
        : undefined;
    return { map, ...(score != null ? { lpIrScore: score } : {}) };
  }

  // ── PDF pagination ─────────────────────────────────────────────────────────
  //
  // Self-estimating, the same way Glucose / Nutrients / Adrenal / Hydration
  // are: the packer needs a height before render. The hero's pieces are fixed
  // SVG boxes rather than text that grows, so those are arithmetic rather than
  // a guess — the only variable up there is whether LP-IR was reported.
  static readonly PDF_HEADER_HEIGHT = 46;
  /**
   * The 5x4 grid: GRID_TOP 32 + 4 rows at 22 + 3 gaps at 3 is a 131px SVG,
   * plus margin. GRID_TOP grew from 27 on 2026-10-26 to seat the "LDL-P" axis
   * title above the column labels.
   */
  static readonly PDF_MAP_HEIGHT = 141;
  /**
   * The readout: TWO bulleted lines now, not one joined line, at 12px on a
   * 1.45 line-height, plus the block's own top margin.
   */
  static readonly PDF_READOUT_HEIGHT = 41;
  /**
   * The LP-IR reading: one line that wraps to two at this width, plus margin.
   *
   * It reserved 164px for part of 2026-10-26, when LP-IR led the
   * panel on the house gauge. Dropping it back is a 134px swing the other way,
   * which only ever OVER-reserves, so it cannot push lipid results under a
   * second copy of the section title the way the under-reservation in the
   * Glucose comment in pdf-section.tsx does.
   */
  static readonly PDF_LP_IR_LINE_HEIGHT = 30;
  /** One ranked bar: label line (14) + track (12) + margin (5). */
  static readonly PDF_AREA_BAR_HEIGHT = 31;
  static readonly PDF_AREA_BARS_MARGIN = 12;
  /*
   * One pattern box: padding and border (16), the title line (16), and the
   * consistent-with line, which wraps. Measured at PDF_CHAR_WIDTH 6.9 — the
   * BOLD glyph width `.condition-section` sets — over the 270px usable width,
   * so about 39 characters a line.
   */
  static readonly PDF_BOX_CHROME = 32;
  static readonly PDF_BOX_LINE_HEIGHT = 15;
  static readonly PDF_BOX_CHARS_PER_LINE = 39;
  static readonly PDF_MARKER_ROW_HEIGHT = 18;

  static estimatedPdfHeight(ba: BloodworkAnalysis): number {
    if (!LipidParticleInsights.shouldRender(ba)) return 0;
    const map = lipidParticleMap(ba);
    const lpIr = ba.lpIrScore;
    const hasLpIr = !!(
      lpIr
      && typeof lpIr.hasValue === 'function'
      && lpIr.hasValue()
    );
    const hasReadout = map?.countValue != null || map?.sizeValueNm != null;

    /*
     * The bars and boxes, which is where the height actually varies. A panel
     * with five assessed areas and four resolved patterns is roughly twice the
     * hero's height, so estimating the hero alone would under-reserve the page
     * and push the lipid results onto a page of their own under a second copy
     * of the title — the failure the Glucose comment in pdf-section.tsx
     * describes.
     */
    const areas = resolveLipidAreas(ba);
    /*
     * Counted off `barAreas`, not off `areas.areas`: Particle Burden drops out
     * whenever the grid already draws LDL-P, and an estimate that still counted
     * five bars would over-reserve by one every time.
     */
    const barCount =
      areas && areas.anyAssessed
        ? LipidParticleInsights.barAreas(map, areas).length
        : 0;
    const barsHeight = barCount
      ? LipidParticleInsights.PDF_AREA_BARS_MARGIN
        + barCount * LipidParticleInsights.PDF_AREA_BAR_HEIGHT
      : 0;
    const boxesHeight = (areas?.patterns ?? []).reduce((total, pattern) => {
      const lines = Math.max(
        1,
        Math.ceil(
          pattern.consistentWith.length
            / LipidParticleInsights.PDF_BOX_CHARS_PER_LINE,
        ),
      );
      return (
        total
        + LipidParticleInsights.PDF_BOX_CHROME
        + lines * LipidParticleInsights.PDF_BOX_LINE_HEIGHT
        + (pattern.markers.length
          ? LipidParticleInsights.PDF_MARKER_ROW_HEIGHT
          : 0)
      );
    }, 0);

    return (
      LipidParticleInsights.PDF_HEADER_HEIGHT
      + LipidParticleInsights.PDF_MAP_HEIGHT
      + (hasReadout ? LipidParticleInsights.PDF_READOUT_HEIGHT : 0)
      + (hasLpIr ? LipidParticleInsights.PDF_LP_IR_LINE_HEIGHT : 0)
      + barsHeight
      + boxesHeight
    );
  }

  /**
   * The LP-IR Score, as a reading line under the pattern boxes.
   *
   * ── Why this is NOT the hero, after being the hero for a few hours ─────
   * Maryann asked for the gauge on 2026-10-26 and withdrew it the same day:
   * "Is this really the right thing to do to show a gauge for reduced insulin
   * sensitivity when we're already figuring that out in sophisticated patterns
   * in the glucose section? ... I don't think a whole lipid section should
   * start with an insulin resistance marker." Three reasons it was right to
   * pull:
   *
   *  1. IT IS NOT WHAT THE PANEL IS FOR. The NMR LipoProfile earns its place
   *     by counting atherogenic particles. The AACC Lipoprotein and Vascular
   *     Diseases working group reviewed 25 studies and 85 outcomes and put it
   *     plainly: particle number is "a strong indicator of future
   *     cardiovascular disease events, even superior to the concentration of
   *     LDL cholesterol" (PMID: 23386699). Otvos 2011 is the reason to run the
   *     test at all: among people whose LDL-C and LDL-P DISAGREE, only LDL-P
   *     tracked events, HR 1.45 (1.19-1.78) against LDL-C's 1.07 (0.88-1.30)
   *     (PMID: 21392724).
   *  2. THE GLUCOSE SECTION ALREADY ANSWERS IT, and better. LP-IR is a
   *     lipid-derived proxy fitted to HOMA-IR at r=0.51 (Shalaurova 2014, PMID: 24959989).
   *     Glucose reads glucose, insulin, HbA1c and C-peptide directly, has its
   *     own gauge, and carries "Reduced Insulin Sensitivity" on about forty
   *     patterns in glucose-pattern-insights.generated.ts. Two gauges in two
   *     sections can disagree, and then the practitioner has two answers and
   *     no tiebreak.
   *  3. IT IS ALREADY ON THIS CARD TWICE. LP-IR leads Area 3's marker list in
   *     LipidParticleAreas.ts and owns pattern 3.1. The gauge was a third and
   *     loudest copy of one marker, above the hero.
   *
   * So the score is still reported, with the lab's band word and the
   * percentile in words, and it is reported as a marker rather than as a
   * verdict. It stays BELOW the pattern boxes on purpose: this panel's
   * headline belongs to the particle count.
   */
  renderLpIrLine(score: number) {
    return (
      <div style={LP_IR_LINE_STYLE}>
        {`LP-IR ${score} (${lpIrBandLabel(score)}), ${lpIrPercentileWording(score)}.`}
      </div>
    );
  }

  /**
   * The areas that get a BAR, which is not always all five of them.
   *
   * Maryann, 2026-10-27: "I don't know why we would repeat the LDL-P in those
   * five bar graphs at the bottom when we already have it at the top." She is
   * right, and it is exactly one bar. Checking each area's lead marker against
   * the grid's two axes:
   *
   *   Particle Burden (LDL-P)          ← the grid's COLUMN axis. Duplicate.
   *   Average Particle Size (LDL Size)   mean diameter, deliberately off the grid
   *   HDL Capacity (HDL-P)               not on the grid
   *   Triglycerides & VLDL (LP-IR)       not on the grid
   *   Inherited Risk (Lp(a))             not on the grid
   *
   * So Particle Burden drops whenever the grid has already drawn it, and the
   * bars become "the rest of the lipid picture" rather than a second telling of
   * the first one. With the top section now labelled Atherogenic Particle
   * Burden, keeping the bar would have said it three times.
   *
   * ⚠ IT COMES BACK WHEN THE GRID CANNOT DRAW IT. Particle Burden also carries
   * apoB and LDL-C, so a client with apoB and no LDL-P has an unread count axis
   * and this bar is the only thing covering burden at all. The condition is the
   * grid's own axis, `map.countValue`, not "is LDL-P present", so the bar and
   * the grid can never disagree about who is showing it.
   */
  static barAreas(
    map: LipidParticleMap,
    areas: LipidAreaAnalysis,
  ): LipidAreaResult[] {
    const ranked = rankedLipidAreas(areas);
    if (map.countValue == null) return ranked;
    return ranked.filter(a => a.area !== 'ParticleBurden');
  }

  /**
   * The header's info POPOVER: what every band word on this card means.
   *
   * Maryann, 2026-10-26: "we're going to need to define the low, below average,
   * above average, and high for small LDLP and how it correlates to the
   * percentiles. We also need to define the borderline high, high, and very
   * high for total LDLP ... if we do a tooltip hover in the Insights panel
   * header that defines all those LDLP and small LDL categories."
   *
   * It lives here rather than in an About row because the words it defines are
   * on THIS card, in three places at once: the headline reading, the grid axes
   * and the ranked bars. An About row would define them a screen away.
   *
   * ⚠ THE TWO AXES ARE CUT AT DIFFERENT PERCENTILES and the tooltip says so
   * per row rather than with one shared ladder: LDL-P at the 20th/50th/80th/95th
   * and Small LDL-P at the 25th/50th/75th, both read off the graph page of
   * Labcorp/NMR LipoProfile+IR+Graph.pdf. The numbers come from
   * LDL_P_BAND_NOTES / SMALL_LDL_P_BAND_NOTES so the tooltip cannot drift away
   * from the bands the grid is drawn from.
   *
   * ⚠ A POPOVER DOES NOT PRINT. The PDF renders this same component, so the
   * icon reaches the export as an icon with nothing behind it. That is the one
   * place this falls short of the standing PDF-parity rule, and the definitions
   * will need a printed home (an About row, or a line under the grid) before
   * this panel ships to prod.
   */
  static renderBandPopover() {
    const row = (n: { label: string; range: string; percentile: string }) => (
      <div key={n.label} style={{ marginTop: '2px' }}>
        <b>{n.label}</b> {n.range} nmol/L ({n.percentile} percentile)
      </div>
    );
    return (
      <OverlayTrigger
        placement="bottom"
        trigger="click"
        rootClose
        overlay={
          <Popover
            id="lipid-particle-bands-popover"
            style={{ maxWidth: 'min(420px, 92vw)', width: 'max-content' }}
          >
            <Popover.Content>
            <div style={{ textAlign: 'left', fontSize: '12px' }}>
              <div><b>Total LDL Particles (LDL-P)</b></div>
              {LDL_P_BAND_NOTES.map(row)}
              <div style={{ marginTop: '10px' }}>
                <b>Small LDL Particles (Small LDL-P)</b>
              </div>
              {SMALL_LDL_P_BAND_NOTES.map(row)}
              <div style={{ marginTop: '10px' }}>{BAND_REFERENCE_NOTE}</div>
            </div>
            </Popover.Content>
          </Popover>
        }
      >
        <FontAwesomeIcon
          icon="info-circle"
          style={{
            marginLeft: '8px',
            cursor: 'pointer',
          }}
        />
      </OverlayTrigger>
    );
  }

  /**
   * Five ranked bars: length is the published value where there is one and the
   * ladder score otherwise, hue is the matching zone.
   */
  renderAreaBars(analysis: LipidAreaAnalysis, map: LipidParticleMap) {
    const ranked = LipidParticleInsights.barAreas(map, analysis);
    if (!ranked.length) return null;
    return (
      <div style={{ marginTop: '12px' }}>
        <div style={AXIS_CAPTION_STYLE}>
          <span>{LIPID_RISK_AXIS_LOW}</span>
          <span>{LIPID_RISK_AXIS_HIGH}</span>
        </div>
        {ranked.map(area => {
          /*
           * Particle Burden carries a published scale, so its fill is the LDL-P
           * value placed on Labcorp's own five printed bands, its ticks are
           * those band boundaries, and its word is the band Labcorp prints for
           * that value. Every other area has no published scale behind it, so
           * it keeps the four-step ladder and its three edges.
           */
          const fill = area.scale ? area.scale.percent : area.score ?? 0;
          const edges = area.scale ? area.scale.edges : BAND_EDGES;
          const label = area.scale
            ? area.scale.label
            : LIPID_BAND_LABEL[area.band];
          return (
            <div key={area.area} style={{ marginBottom: '5px' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: '10.5px',
                  color: '#000',
                }}
              >
                <span style={{ fontWeight: 600 }}>
                  {LIPID_AREA_SHORT[area.area]}
                </span>
                <span style={{ color: BAND_TEXT[area.band] }}>{label}</span>
              </div>
              <div
                style={{
                  position: 'relative',
                  height: `${BAR_HEIGHT}px`,
                  background: TRACK_BG,
                  boxShadow: TRACK_INSET,
                  borderRadius: '2px',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${fill}%`,
                    height: '100%',
                    background: BAND_FILL[area.band],
                  }}
                />
                {edges.map(edge => (
                  <div
                    key={edge}
                    style={{
                      position: 'absolute',
                      left: `${edge}%`,
                      top: 0,
                      bottom: 0,
                      width: '1px',
                      background: 'rgba(0,0,0,0.22)',
                    }}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  /**
   * One box per matched pattern, in area order.
   *
   * A HOLD box is drawn in the same `.condition-section` as a flag, because the
   * practitioner needs to read it with the same weight: "this marker is above
   * range and the particle data does not carry it" is a finding, not an absence
   * of one. What distinguishes it is the label, not the styling.
   */
  renderPatternBoxes(resolved: ResolvedPattern[]) {
    if (!resolved.length) return null;
    return (
      <>
        {resolved.map(({ pattern, markers }) => (
          <div className="condition-section" key={pattern.id}>
            <div style={{ color: 'black', textAlign: 'left' }}>
              {pattern.title}
              {pattern.effect === 'hold' ? (
                <span style={HOLD_TAG_STYLE}>explains</span>
              ) : null}
            </div>
            <div style={CONSISTENT_WITH_STYLE}>{pattern.consistentWith}</div>
            {markers.length ? (
              <div style={MARKER_ROW_STYLE}>
                {markers.map(marker => (
                  <span style={{ whiteSpace: 'nowrap' }} key={marker.key}>
                    {marker.direction === 'level' ? (
                      <span style={LEVEL_STYLE}>=</span>
                    ) : (
                      <img
                        src={ARROW[marker.direction]}
                        alt={ARROW_ALT[marker.direction]}
                        style={arrowStyle}
                      />
                    )}
                    {marker.label}
                  </span>
                ))}
              </div>
            ) : null}
          </div>
        ))}
      </>
    );
  }

  render() {
    const { bloodworkAnalysis } = this.props;
    if (!nmrLipoProfileVisible()) return null;

    const map = lipidParticleMap(bloodworkAnalysis);
    if (!map) return null;

    const lpIr = bloodworkAnalysis.lpIrScore;
    const score =
      lpIr && typeof lpIr.hasValue === 'function' && lpIr.hasValue()
        ? lpIr.value
        : undefined;
    const areas = resolveLipidAreas(bloodworkAnalysis);

    return (
      <Card className="evaluationPanel" id="lipid-particle-insights">
        {/*
          "Lipid Insights", not "Lipid Particle Insights" (Maryann, 2026-10-27:
          "remove particle because we're talking about other markers like Lp(a),
          apoB, apoA, and the ratio ... other markers that we may get, like
          Boston Heart's heart map and their cholesterol types, so let's leave
          it generic").

          The narrower name was already wrong rather than merely limiting: three
          of the four ranked bars read markers that are not particle counts at
          all. Inherited Risk is Lp(a) and the ApoB/ApoA-1 ratio, HDL Capacity
          reads Apo A-1, and Triglycerides & VLDL reads triglycerides and the
          TG/HDL ratio. Only the hero is strictly about particles.

          \u26a0 THE DOM id, THE CLASS AND THE FILE NAMES KEEP "particle". Renaming
          them would churn four files' imports for nothing a user sees, and the
          same rule the Results Overview title follows applies here: the title
          is copy, the identifier is not. See [[project_results_overview_no_method_title]].
        */}
        <Card.Header className="evaluationPanelHeading">
          Lipid Insights
          {LipidParticleInsights.renderBandPopover()}
        </Card.Header>
        <Card.Body>
          <div className="anemiaStatusContainer">
            <LipidParticleHero map={map} />
            {areas && areas.anyAssessed ? this.renderAreaBars(areas, map) : null}
            {areas ? this.renderPatternBoxes(areas.resolved) : null}
            {score != null ? this.renderLpIrLine(score) : null}
            {score != null ? (
              <div style={FOOTNOTE_STYLE}>{LP_IR_FOOTNOTE}</div>
            ) : null}
          </div>
        </Card.Body>
      </Card>
    );
  }
}
